import { createReducer, on } from '@ngrx/store';
import * as SquareDeviceLocationActions from './square-device-location.actions';
import { SquareDeviceLocation } from './square-device-location.models';

// ----------------------------------------------------------------------
// @ States
// ----------------------------------------------------------------------

export interface SquareDeviceLocationsState {
    squareDeviceLocations: SquareDeviceLocation[];
    squareDeviceLocation: SquareDeviceLocation | null;
    isLoading: boolean;
    error: string | null;
}

// ----------------------------------------------------------------------
// @ Initial States
// ----------------------------------------------------------------------

export const initialSquareDeviceLocationsState: SquareDeviceLocationsState = {
    squareDeviceLocations: [],
    squareDeviceLocation: null,
    isLoading: false,
    error: null,
};

// ----------------------------------------------------------------------
// @ Reducers
// ----------------------------------------------------------------------

export const squareDeviceLocationsReducer = createReducer(
    initialSquareDeviceLocationsState,

    // Load All
    on(SquareDeviceLocationActions.loadSquareDeviceLocations, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        SquareDeviceLocationActions.loadSquareDeviceLocationsSuccess,
        (state, { data }) => ({
            ...state,
            isLoading: false,
            squareDeviceLocations: data,
        })
    ),
    on(
        SquareDeviceLocationActions.loadSquareDeviceLocationsFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error,
        })
    ),

    // Load One
    on(SquareDeviceLocationActions.loadSquareDeviceLocation, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        SquareDeviceLocationActions.loadSquareDeviceLocationSuccess,
        (state, { data }) => ({
            ...state,
            isLoading: false,
            squareDeviceLocation: data,
        })
    ),
    on(
        SquareDeviceLocationActions.loadSquareDeviceLocationFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error,
        })
    ),

    // Create
    on(SquareDeviceLocationActions.createSquareDeviceLocation, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        SquareDeviceLocationActions.createSquareDeviceLocationSuccess,
        (state, { data }) => ({
            ...state,
            isLoading: false,
            squareDeviceLocations: [
                ...(state.squareDeviceLocations || []),
                ...data,
            ],
        })
    ),
    on(
        SquareDeviceLocationActions.createSquareDeviceLocationFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error,
        })
    ),

    // Update
    on(SquareDeviceLocationActions.updateSquareDeviceLocation, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        SquareDeviceLocationActions.updateSquareDeviceLocationSuccess,
        (state, { data }) => ({
            ...state,
            isLoading: false,
            squareDeviceLocations: state.squareDeviceLocations.map((s) =>
                s.id === data.id ? data : s
            ),
        })
    ),
    on(
        SquareDeviceLocationActions.updateSquareDeviceLocationFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error,
        })
    ),

    // Delete
    on(SquareDeviceLocationActions.deleteSquareDeviceLocation, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        SquareDeviceLocationActions.deleteSquareDeviceLocationSuccess,
        (state, { locationId }) => ({
            ...state,
            isLoading: false,
            squareDeviceLocations: state.squareDeviceLocations.filter(
                (location) => location.id !== locationId
            ),
        })
    ),
    on(
        SquareDeviceLocationActions.deleteSquareDeviceLocationFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error,
        })
    ),

    // Delete Item
    on(SquareDeviceLocationActions.deleteSquareDeviceLocationItem, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        SquareDeviceLocationActions.deleteSquareDeviceLocationItemSuccess,
        (state, { id, locationId }) => ({
            ...state,
            isLoading: false,
            squareDeviceLocations: state.squareDeviceLocations.map(
                (location) =>
                    location.id === locationId
                        ? {
                              ...location,
                              children: location.children.filter(
                                  (child) => child.id !== id
                              ),
                          }
                        : location
            ),
        })
    ),

    on(
        SquareDeviceLocationActions.deleteSquareDeviceLocationItemFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error,
        })
    )
);
